import type { QueryClient } from '@tanstack/react-query';
import type { ActorRefFrom } from 'xstate';
import { agentSnapshotQueryOptions } from '../features/presence/api.js';
import type { agentSessionMachine } from './agent.machine.js';

export interface ReconcilerDeps {
  readonly queryClient: QueryClient;
  readonly agentSession: ActorRefFrom<typeof agentSessionMachine>;
  /** Whether this user has an agent at all; an administrator has no agent snapshot to read. */
  readonly isAgent: () => boolean;
}

/**
 * What the workspace does when a replay could not cover the gap.
 *
 * Everything on screen was true before the gap and nothing has confirmed it since, so the whole
 * cache is suspect and all of it is invalidated — TanStack then refetches the queries that are
 * actually being shown and leaves the rest to be read when something asks. That is cheaper to
 * reason about than a list of which aggregates were affected, and it is also more honest: the one
 * thing a client in this position knows is that it does not know.
 *
 * The agent's snapshot is awaited specifically, because the connection chart may not leave
 * `resyncing` until the state the agent acts on is the server's. It is sent as `reconcile` rather
 * than as `snapshot`, which drops the Offers the workspace was holding: after a gap nothing confirms
 * them, so an Offer that expired while the socket was down is gone by the time the banner clears
 * (real-time updates §4).
 */
export function createSnapshotReconciler(deps: ReconcilerDeps) {
  return async function reconcile(): Promise<void> {
    await deps.queryClient.invalidateQueries();
    if (!deps.isAgent()) {
      return;
    }
    const snapshot = await deps.queryClient.fetchQuery({
      ...agentSnapshotQueryOptions(),
      // Whatever is cached was true before the gap. Nothing stale may answer this.
      staleTime: 0,
    });
    deps.agentSession.send({ type: 'reconcile', snapshot });
  };
}
